import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Get_use_type_name, Truncar_dispositivo, DateTime_to_string, Get_today_range } from '../Comun';
import ModalEliminacion from '../modal/ModalEliminacion';
import FilterEscaneoEventoModal from '../modal/FilterEscaneoEventoModal';
import { AuthContext } from '../contexts/AuthContext';
import { apiFetch } from '../utils/apiFetch';
import { useParams, useNavigate } from 'react-router-dom';

interface Scan
{
    id: number;
    code: string;
    response: string;
    type_name: string;
    tag: string;
    device: string;
    accepted: boolean;
    use_type: number;
    dt_use: string;
    id_provider: number;
}

interface Tag
{
    id: number;
    tag: string;
    valid_time: string;
    type_tag: number;
}

interface Provider
{
    id: number;
    name: string;
}

const EscaneosEvento: React.FC = () =>
{
    const { eventId, clientId } = useParams();
    const navigate = useNavigate();
    const eventIdNum = Number(eventId);
    const { token } = useContext(AuthContext);
    const [scans, setScans] = useState<Scan[]>([]);
    const [tags, setTags] = useState<Tag[]>([]);
    const [providers, setProviders] = useState<Provider[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedScan, setSelectedScan] = useState<Scan | null>(null);
    const [isUnregisterByFilter, setUnregisterByFilter] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalResults, setTotalResults] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [title, setEventName] = useState<string>('');
    const [expandedScanId, setExpandedScanId] = useState<number | null>(null);

    const [filters, setFilters] = useState<{
        is_root_response: boolean;
        response: string | null;
        code: string | null;
        type: number | null;
        id_tag: number | null;
        accepted: number | null;
        start_dt: string | null;
        end_dt: string | null;
    }>({
        is_root_response: false,
        response: null,
        code: null,
        type: null,
        id_tag: null,
        accepted: null,
        start_dt: null,
        end_dt: null
    });

    //-----------------------------------------------------------

    const fetchScans = useCallback(async (page: number = 1) =>
    {
        setLoading(true);
        try
        {
            const queryParams = new URLSearchParams({
                id_event: eventIdNum.toString(),
                page: page.toString(),
                ...(filters.code && { code: filters.code }),
                ...(filters.response && { [filters.is_root_response ? 'root_response' : 'response']: filters.response }),
                ...(filters.type !== null && { type: filters.type.toString() }),
                ...(filters.id_tag !== null && { tag: filters.id_tag.toString() }),
                ...(filters.accepted !== null && { accepted: filters.accepted.toString() }),
                ...(filters.start_dt && { start_dt: filters.start_dt }),
                ...(filters.end_dt && { end_dt: filters.end_dt })
            });

            const response = await apiFetch(`web/search_registered_codes?${queryParams.toString()}`, {
                method: 'GET'
            });

            if (response.ok)
            {
                const data = await response.json();
                setScans(data.results);
                setTotalResults(data.total_results);
                setTotalPages(data.total_pages);
                setCurrentPage(page);
            }
            else
            {
                throw new Error('Error al cargar escaneos.');
            }
        }
        catch (error)
        {
            const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
            setError(errorMsg);
        }
        finally
        {
            setLoading(false);
        }
    }, [eventId, filters, token]);

    //-----------------------------------------------------------

    const fetchTags = useCallback(async () =>
    {
        try
        {
            const response = await apiFetch(`web/get_event_tags?id_event=${eventId}&id_client=${clientId}`, {
                method: 'GET'
            });

            if (response.ok)
            {
                const data = await response.json();
                setTags(data.tags);
                setEventName(data.eventName);
            }
            else
            {
                navigate('/main/not-found');
            }
        }
        catch (error)
        {
            const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
            setError(errorMsg);
        }
    }, [eventId, token]);

    //-----------------------------------------------------------

    const fetchProviders = useCallback(async () =>
    {
        try
        {
            const response = await apiFetch(`web/get_event_providers_basic_info?id_event=${eventId}`, {
                method: 'GET'
            });

            if (response.ok)
            {
                const data = await response.json();
                setProviders(data);
            }
            else
            {
                throw new Error('Error al cargar providers.');
            }
        }
        catch (error)
        {
            const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
            setError(errorMsg);
        }
    }, [eventId, token]);

    //-----------------------------------------------------------

    const fetchSyncCodereadr = useCallback(async () =>
    {
        setLoading(true);
        try
        {
            const queryParams = new URLSearchParams({
                id_event: eventIdNum.toString(),
            });

            const response = await apiFetch(`web/sync_scans_codereadr?${queryParams.toString()}`, {
                method: 'GET'
            });

            if (response.ok)
            {
                fetchScans(1);
            }
            else
            {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Error al sincronizar con CodeREADr.');
            }
        }
        catch (error)
        {
            const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
            setError(errorMsg);
        }
        finally
        {
            setLoading(false);
        }
    }, [eventId, filters, token]);

    //-----------------------------------------------------------

    useEffect(() =>
    {
        const loadInitialData = async () =>
        {
            try
            {
                await fetchTags();
                await fetchProviders();
            }
            catch (error)
            {
                setError('Error al cargar datos iniciales.');
            }
        };

        loadInitialData();
    }, [fetchTags, fetchProviders]);

    //-----------------------------------------------------------

    useEffect(() =>
    {
        fetchScans(currentPage);
    }, [fetchScans, currentPage]);

    //-----------------------------------------------------------

    const i_Unregister_selected_code = async () =>
    {
        const formData = new URLSearchParams();
        formData.append('id', selectedScan?.id.toString() || '');

        const response = await apiFetch(`web/unregister_code`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: formData.toString()
        });

        if (response.ok)
            fetchScans(1);
        else
            throw new Error('Error al desregistar el código.');
    }

    //-----------------------------------------------------------

    const i_Unregister_codes_by_filter = async () =>
    {
        const queryParams = new URLSearchParams({
            id_event: eventIdNum.toString(),
            ...(filters.code && { code: filters.code }),
            ...(filters.response && { [filters.is_root_response ? 'root_response' : 'response']: filters.response }),
            ...(filters.type !== null && { type: filters.type.toString() }),
            ...(filters.id_tag !== null && { tag: filters.id_tag.toString() }),
            ...(filters.accepted !== null && { accepted: filters.accepted.toString() }),
            ...(filters.start_dt && { start_dt: filters.start_dt }),
            ...(filters.end_dt && { end_dt: filters.end_dt }),
        });

        const response = await apiFetch(`web/unregister_codes_by_filter?${queryParams.toString()}`, {
            method: 'GET'
        });

        if (response.ok)
            fetchScans(1);
        else
            throw new Error('Error al desregistar códigos.');
    }

    //-----------------------------------------------------------

    const handleConfirmUnregister = async () =>
    {
        setIsModalOpen(false);
        try
        {
            if (isUnregisterByFilter === true)
                await i_Unregister_codes_by_filter();
            else
                await i_Unregister_selected_code();
        }
        catch (error)
        {
            setError('Error al desregistrar.');
        }
    };

    const handleCancelUnregister = () => { setIsModalOpen(false); };

    const handleUnregisterClick = (scan: Scan) =>
    {
        setUnregisterByFilter(false);
        setSelectedScan(scan);
        setIsModalOpen(true);
    };

    const handleUnregisterFilteredClick = () =>
    {
        setUnregisterByFilter(true);
        setIsModalOpen(true);
    };

    const handleDownloadFilteredClick = async () =>
    {
        const queryParams = new URLSearchParams({
            id_event: eventIdNum.toString(),
            id_client: Number(clientId).toString(),
            'ignore_client_event': 'true',
            ...(filters.code && { code: filters.code }),
            ...(filters.response && { [filters.is_root_response ? 'root_response' : 'response']: filters.response }),
            ...(filters.type !== null && { type: filters.type.toString() }),
            ...(filters.id_tag !== null && { tag: filters.id_tag.toString() }),
            ...(filters.accepted !== null && { accepted: filters.accepted.toString() }),
            ...(filters.start_dt && { start_dt: filters.start_dt }),
            ...(filters.end_dt && { end_dt: filters.end_dt }),
        });

        try
        {
            const response = await apiFetch(`web/export_search_registered_codes?${queryParams.toString()}`, {
                method: 'GET'
            });

            if (!response.ok)
                throw new Error('Error al descargar el archivo CSV.');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'export.csv';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        }
        catch (error)
        {
            const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
            setError(errorMsg);
        }
    };

    const i_Message_warning_unregister = () =>
    {
        if (isUnregisterByFilter === true)
            return (<p>Los escaneos mostrados van a ser borrados</p>);
        else
            return (<p>El ÚLTIMO REGISTRO del código <b>{selectedScan?.code}</b> va a ser borrado</p>);
    }

    const handleFilterOpen = () => { setIsFilterModalOpen(true); };

    const handleApplyFilters = (filters: {
        is_root_response: boolean;
        response: string | null;
        code: string | null;
        type: number | null;
        id_tag: number | null;
        accepted: number | null;
        start_dt: string | null;
        end_dt: string | null;
    }) =>
    {
        setFilters(filters);
        setCurrentPage(1);
    };

    const handleClearFilters = () =>
    {
        setFilters({
            is_root_response: false,
            response: null,
            code: null,
            type: null,
            id_tag: null,
            accepted: null,
            start_dt: null,
            end_dt: null
        });
        setCurrentPage(1);
    };

    const handlePageChange = (newPage: number) =>
    {
        if (newPage > 0 && newPage <= totalPages)
            setCurrentPage(newPage);
    };

    const handleToggleExpand = (index: number) =>
    {
        setExpandedScanId(prev => prev === index ? null : index);
    };

    const handleSyncCodereadrClick = async () =>
    {
        try
        {
            await fetchSyncCodereadr();
        }
        catch (error)
        {
            setError('Error al sincronizar.');
        }
    };
    //-----------------------------------------------------------

    return (
        <div className="eventos-container">
            <div className="miguitas">Escaneos de {title}</div>
            <div className="content-wrapper">

                {/* ── Toolbar ── */}
                <div className="cod-toolbar">
                    <button className="icon-btn" onClick={() => navigate(`/main/recintos/${clientId}/eventos`)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M15 19l-7-7 7-7"/>
                        </svg>
                        Volver a Eventos
                    </button>
                    <div className="cod-toolbar__right">
                        <button className="icon-btn" onClick={handleFilterOpen}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <path d="M3 6h18M7 12h10M11 18h2"/>
                            </svg>
                            Filtro
                        </button>
                        <button className="icon-btn" onClick={handleSyncCodereadrClick}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" >
                                <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                                <path d="M3 3v5h5"/>
                                <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/>
                                <path d="M16 16h5v5"/>
                            </svg>
                            Sincronizar con CodeREADr
                        </button>
                        <button className="icon-btn" onClick={handleDownloadFilteredClick}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <path d="M12 3v13M7 11l5 5 5-5M3 21h18"/>
                            </svg>
                            Descargar filtrados
                        </button>
                        <button className="icon-btn icon-btn--danger" onClick={handleUnregisterFilteredClick}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <path d="M3 10h13a5 5 0 0 1 0 10H7"/>
                                <path d="M6 6l-3 4 3 4"/>
                            </svg>
                            Desescanear filtrados
                        </button>
                    </div>
                </div>

                {/* ── Errores ── */}
                {error && <p className="error-message">{error}</p>}

                {/* ══ DESKTOP: tabla ══ */}
                <div className="codes-container desktop-only">
                    {loading && !error && <p className="loading-message">Obteniendo escaneos...</p>}
                    {!loading && !error && scans.length === 0 && <p>No hay escaneos para mostrar.</p>}
                    {!loading && !error && scans.length > 0 && (
                        <div className="table-scroll">
                            <table className="eventos-table">
                                <thead>
                                    <tr>
                                        <th>Código</th>
                                        <th>Tipo</th>
                                        <th>Tag</th>
                                        <th>Respuesta</th>
                                        <th>Dispositivo</th>
                                        <th>Resultado</th>
                                        <th>Fecha y hora</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {scans.map(scan => {
                                        const formattedDateTime = DateTime_to_string(new Date(scan.dt_use));
                                        return (
                                            <tr key={scan.id}>
                                                <td className="cod-code-cell">{scan.code}</td>
                                                <td className="cod-muted-cell">{scan.type_name}</td>
                                                <td className="cod-muted-cell">{scan.tag}</td>
                                                <td className="cod-muted-cell">{scan.response}</td>
                                                <td className="cod-muted-cell" title={scan.device}>{Truncar_dispositivo(scan.device)}</td>
                                                <td className="cod-muted-cell">
                                                    <span className={scan.accepted ? 'ev-pill ev-pill--green' : 'ev-pill ev-pill--red'}>{Get_use_type_name(scan.use_type)}</span>
                                                </td>
                                                <td className="cod-muted-cell">{formattedDateTime}</td>
                                                <td className="evento-actions">
                                                    <button
                                                        className="icon-btn icon-btn--unscan"
                                                        onClick={() => handleUnregisterClick(scan)}
                                                        disabled={!scan.accepted}>
                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                            <path d="M3 10h13a5 5 0 0 1 0 10H7"/>
                                                            <path d="M6 6l-3 4 3 4"/>
                                                        </svg>
                                                        Desescanear
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* ══ MOBILE: cards colapsables ══ */}
                <div className="mobile-only">
                    {loading && !error && <p className="loading-message">Obteniendo escaneos...</p>}
                    {!loading && !error && scans.length === 0 && <p>No hay escaneos para mostrar.</p>}
                    {!loading && !error && scans.length > 0 && (
                        <div className="scan-cards">
                            {scans.map((scan, index) => {
                                const formattedDateTime = DateTime_to_string(new Date(scan.dt_use));
                                const isOpen = expandedScanId === index;
                                return (
                                    <div key={index} className="scan-card">
                                        <div
                                            className="scan-card__header"
                                            onClick={() => handleToggleExpand(index)}>
                                            <span className={`scan-dot ${scan.accepted ? 'scan-dot--ok' : 'scan-dot--err'}`}></span>
                                            <span className="scan-card__code">{scan.code}</span>
                                            <span className="scan-card__date">{formattedDateTime}</span>
                                            <span className={`scan-badge ${scan.accepted ? 'scan-badge--ok' : 'scan-badge--err'}`}>
                                                {Get_use_type_name(scan.use_type)}
                                            </span>
                                            <svg
                                                width="14" height="14"
                                                viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" strokeWidth="2.5"
                                                className={`scan-chevron ${isOpen ? 'scan-chevron--open' : ''}`}>
                                                <path d="M6 9l6 6 6-6"/>
                                            </svg>
                                        </div>

                                        {isOpen && (
                                            <>
                                                <div className="scan-card__detail">
                                                    <div className="scan-detail-row">
                                                        <span className="scan-detail-row__label">Code</span>
                                                        <span className="scan-detail-row__val">{scan.code}</span>
                                                    </div>
                                                    <div className="scan-detail-row">
                                                        <span className="scan-detail-row__label">Tipo</span>
                                                        <span className="scan-detail-row__val">{scan.type_name}</span>
                                                    </div>
                                                    <div className="scan-detail-row">
                                                        <span className="scan-detail-row__label">Tag</span>
                                                        <span className="scan-detail-row__val">{scan.tag}</span>
                                                    </div>
                                                    <div className="scan-detail-row">
                                                        <span className="scan-detail-row__label">Respuesta</span>
                                                        <span className="scan-detail-row__val">{scan.response}</span>
                                                    </div>
                                                    <div className="scan-detail-row">
                                                        <span className="scan-detail-row__label">Dispositivo</span>
                                                        <span className="scan-detail-row__val" title={scan.device}>{Truncar_dispositivo(scan.device)}</span>
                                                    </div>
                                                </div>
                                                <div className="scan-card__footer">
                                                    <button
                                                        className="icon-btn icon-btn--unscan scan-unscan-btn"
                                                        onClick={() => handleUnregisterClick(scan)}
                                                        disabled={!scan.accepted}>
                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                            <path d="M3 10h13a5 5 0 0 1 0 10H7"/>
                                                            <path d="M6 6l-3 4 3 4"/>
                                                        </svg>
                                                        Desescanear
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* ── Paginación ── */}
                {!loading && scans.length > 0 && (
                    <div className="pagination-controls">
                        <div className="pagination-buttons">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage <= 1}>
                                <i className="fas fa-caret-left"></i>
                            </button>
                            <span>{currentPage.toLocaleString('es-ES')} / {totalPages.toLocaleString('es-ES')}</span>
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage >= totalPages}>
                                <i className="fas fa-caret-right"></i>
                            </button>
                        </div>
                        <div className="pagination-info">
                            <span>{totalResults.toLocaleString('es-ES')} resultados</span>
                        </div>
                    </div>
                )}
            </div>

            {isModalOpen && (
                <ModalEliminacion
                    isOpen={isModalOpen}
                    onClose={handleCancelUnregister}
                    onConfirm={handleConfirmUnregister}>
                    {i_Message_warning_unregister()}
                </ModalEliminacion>
            )}

            {isFilterModalOpen && (
                <FilterEscaneoEventoModal
                    isOpen={isFilterModalOpen}
                    onClose={() => setIsFilterModalOpen(false)}
                    onApplyFilters={handleApplyFilters}
                    onClearFilters={handleClearFilters}
                    tags={tags}
                    providers={providers}
                    currentFilters={filters}
                />
            )}
        </div>
    );
};

export default EscaneosEvento;